import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { productAttributesEndpoint } from './use-get-attribute-dictionary-api';

interface ResponseData {
  // catalogCategoryIds currently assigned to the attribute type.
  data: number[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string, number | undefined, string];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

// GET ums/v1/cms/master-product-attributes/types/:typeId/categories
export function useGetAttributeTypeCategoriesApi(typeId?: number, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  // Destructure to compose caller enabled with built-in guards (never replace)
  const { enabled: callerEnabled, ...restOptions } = options ?? {};

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${productAttributesEndpoint}/types/${typeId}/categories`);

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([`${productAttributesEndpoint}/types`, typeId, 'categories'], queryFn, {
    enabled: (callerEnabled ?? true) && isLoggedIn && typeId != null,
    retry: false,
    ...restOptions,
  });
}
